import {
  ChangeDetectionStrategy,
  Component,
  computed,
  ElementRef,
  ExistingProvider,
  forwardRef,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

let nextUniqueId = 0;

export const RANGE_INPUT_CONTROL_VALUE_ACCESSOR: ExistingProvider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => RangeInputComponent),
  multi: true,
};

@Component({
  selector: 'sb-range-input',
  standalone: true,
  templateUrl: './range-input.component.html',
  styleUrl: './range-input.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [RANGE_INPUT_CONTROL_VALUE_ACCESSOR],
  exportAs: 'sbRangeInput',
})
export class RangeInputComponent implements ControlValueAccessor {
  readonly min = input<number>(0);
  readonly max = input<number>(10);
  readonly step = input<number>(1);
  readonly id = input<string>(`sb-range-input-${nextUniqueId++}`);

  readonly totalTicks = computed(() => {
    const min = this.min();
    const max = this.max();
    const step = this.step();
    return Math.floor((max - min) / step) + 1;
  });

  readonly ticks = computed(() => {
    return Array(this.totalTicks())
      .fill(0)
      .map((_, i) => i);
  });

  readonly disabled = signal(false);

  readonly value = signal<number>(0);

  // Implemented as part of ControlValueAccessor.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onChange: (value: any) => void = () => {};

  // Implemented as part of ControlValueAccessor.
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  private onTouched: () => void = () => {};

  readonly inputElement = viewChild.required<ElementRef<HTMLInputElement>>('inputElement');

  // Implemented as part of ControlValueAccessor.
  writeValue(value: number): void {
    this.value.set(value);
  }

  // Implemented as part of ControlValueAccessor.
  registerOnChange(fn: (value: any) => void): void {
    this.onChange = fn;
  }

  // Implemented as part of ControlValueAccessor.
  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // Implemented as part of ControlValueAccessor.
  setDisabledState(disabled: boolean): void {
    this.disabled.set(disabled);
  }

  onChangeInput(): void {
    const value = this.inputElement().nativeElement.valueAsNumber;
    this.value.set(value);
    this.onChange(value);
  }

  onBlurInput(): void {
    this.onTouched();
  }
}
